import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { BlogTemplate_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaFilter,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";
import "./JobModal.css";

export default function BlogTemplates() {
  const { userData } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    template_id: [],
    template_name: [],
    template_description: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // "view", "edit", "add"
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch all templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${BlogTemplate_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch templates");
        const data = await response.json();
        setTemplates(data);
      } catch (error) {
        console.error("Error fetching templates:", error);
        Swal.fire("Error", "Failed to load templates.", "error");
      }
    };
    fetchTemplates();
  }, [userData.access_token]);

  // Filter logic
  const getDistinctValues = (column) => {
    const values = templates.map((item) => item[column]);
    return [...new Set(values)].sort((a, b) =>
      column === "template_id"
        ? a - b
        : a?.toString().localeCompare(b?.toString())
    );
  };

  const applyFilters = (item) => {
    return (
      (filters.template_id.length === 0 ||
        filters.template_id.includes(item.template_id)) &&
      (filters.template_name.length === 0 ||
        filters.template_name.includes(item.template_name)) &&
      (filters.template_description.length === 0 ||
        filters.template_description.includes(item.template_description))
    );
  };

  const filteredTemplates = templates.filter(applyFilters);
  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (
      selectedRows.length === filteredTemplates.length &&
      filteredTemplates.length > 0
    ) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredTemplates.map((t) => t.template_id));
    }
  };

  // Filter popup
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prev) => ({ ...prev, [showFilterPopup]: newSelected }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;
    const result = await Swal.fire({
      text: `Delete ${selectedRows.length} template(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });
    if (result.isConfirmed) {
      try {
        await Promise.all(
          selectedRows.map((id) =>
            fetch(`${BlogTemplate_API}/delete/${id}`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${userData.access_token}` },
            }).then((res) => {
              if (!res.ok) throw new Error("Delete failed");
            })
          )
        );
        setTemplates(
          templates.filter((t) => !selectedRows.includes(t.template_id))
        );
        setSelectedRows([]);
        Swal.fire("Deleted!", "Templates removed.", "success");
      } catch (err) {
        Swal.fire("Error", "Failed to delete some templates.", "error");
      }
    }
  };

  // Add / View
  const addTemplate = () => {
    setSelectedTemplate({
      template_name: "",
      template_code: "",
      template_description: "",
      template_schema: {},
      template_preview: "",
    });
    setModalMode("edit");
    setShowModal(true);
  };

  const viewTemplate = (template) => {
    setSelectedTemplate(template);
    setModalMode("view");
    setShowModal(true);
  };

  // Save
  const handleSave = async (updatedTemplate) => {
    console.log("Sending to backend:", updatedTemplate.template_schema);
    console.log("Type:", typeof updatedTemplate.template_schema);
    try {
      let response;
      if (updatedTemplate.template_id) {
        response = await fetch(
          `${BlogTemplate_API}/update/${updatedTemplate.template_id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userData.access_token}`,
            },
            body: JSON.stringify(updatedTemplate),
          }
        );
      } else {
        response = await fetch(`${BlogTemplate_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
          },
          body: JSON.stringify(updatedTemplate),
        });
      }

      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "Save failed");
      }

      const saved = await response.json();
      if (updatedTemplate.template_id) {
        setTemplates(
          templates.map((t) =>
            t.template_id === saved.template_id ? saved : t
          )
        );
      } else {
        setTemplates([...templates, saved]);
      }
      setShowModal(false);
      Swal.fire("Success!", "Template saved successfully.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Failed to save template.", "error");
    }
  };

  const deleteTemplate = async (id) => {
    const result = await Swal.fire({
      title: "Delete this template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    });
    if (result.isConfirmed) {
      try {
        await fetch(`${BlogTemplate_API}/delete/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${userData.access_token}` },
        });
        setTemplates(templates.filter((t) => t.template_id !== id));
        Swal.fire("Deleted!", "", "success");
      } catch (err) {
        Swal.fire("Error", "Failed to delete.", "error");
      }
    }
  };

  const renderFilterPopup = (column) => {
    const displayName =
      column === "template_id"
        ? "ID"
        : column === "template_name"
        ? "Name"
        : "Description";
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter((val) =>
      val?.toString().toLowerCase().includes(searchInput.toLowerCase())
    );

    return (
      <div className="filter-popup">
        <div className="filter-header">
          <span>{displayName}</span>
          <span
            className="clear-filter"
            onClick={() => {
              setFilters((prev) => ({ ...prev, [column]: [] }));
              setShowFilterPopup(null);
            }}
          >
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div
          className="suggestions-list"
          style={{ maxHeight: "200px", overflowY: "auto" }}
        >
          {filteredValues.map((val) => (
            <label key={val}>
              <input
                type="checkbox"
                checked={tempSelected.includes(val)}
                onChange={() => {
                  setTempSelected((prev) =>
                    prev.includes(val)
                      ? prev.filter((v) => v !== val)
                      : [...prev, val]
                  );
                }}
              />
              {val?.toString() || "N/A"}
            </label>
          ))}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Blog Templates - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>Blog Templates</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredTemplates.length &&
                    filteredTemplates.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                ID
                <FaFilter
                  className={`filter-icon ${
                    filters.template_id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("template_id")}
                />
                {showFilterPopup === "template_id" &&
                  renderFilterPopup("template_id")}
              </th>
              <th style={{ position: "relative" }}>
                Name
                <FaFilter
                  className={`filter-icon ${
                    filters.template_name.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("template_name")}
                />
                {showFilterPopup === "template_name" &&
                  renderFilterPopup("template_name")}
              </th>
              <th style={{ position: "relative" }}>
                Description
                <FaFilter
                  className={`filter-icon ${
                    filters.template_description.length > 0
                      ? "active-filter"
                      : ""
                  }`}
                  onClick={() => handleFilterClick("template_description")}
                />
                {showFilterPopup === "template_description" &&
                  renderFilterPopup("template_description")}
              </th>
              <th>Preview</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTemplates.map((template) => (
              <tr
                key={template.template_id}
                className={
                  selectedRows.includes(template.template_id) ? "selected" : ""
                }
                onDoubleClick={() => toggleRowSelection(template.template_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(template.template_id)}
                    onChange={() => toggleRowSelection(template.template_id)}
                  />
                </td>
                <td>{template.template_id}</td>
                <td>{template.template_name}</td>
                <td>
                  {(template.template_description || "-").substring(0, 100)}
                  {(template.template_description || "").length > 100
                    ? "..."
                    : ""}
                </td>
                <td className="tem-preview-cell">
                  {template.template_preview ? (
                    <img
                      src={template.template_preview}
                      alt="Preview"
                      className="tem-preview-thumb"
                      onClick={() => setPreviewImage(template.template_preview)}
                    />
                  ) : (
                    <span className="tem-no-preview">No preview</span>
                  )}
                </td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => viewTemplate(template)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaEdit title="Edit/View" />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteTemplate(template.template_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash title="Delete" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(val) => {
          setItemsPerPage(val);
          setCurrentPage(1);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash /> ({selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addTemplate}>
        <FaPlus />
      </button>

      {/* Preview Image Zoom Modal */}
      {previewImage && (
        <div
          className="tem-preview-modal-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <div className="tem-preview-modal-content">
            <button
              className="tem-preview-close-btn"
              onClick={() => setPreviewImage(null)}
            >
              ×
            </button>
            <img
              src={previewImage}
              alt="Full Preview"
              className="tem-preview-full-image"
            />
          </div>
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={selectedTemplate}
          mode={modalMode}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          setMode={setModalMode}
        />
      )}
    </div>
  );
}

// ==================== MODAL ====================
function TemplateModal({ template, mode, onClose, onSave, setMode }) {
  const [editTemplate, setEditTemplate] = useState(template);
  const isEdit = mode === "edit";
  const isAdd = !template?.template_id;

  // Parse schema when opening modal
  useEffect(() => {
    if (template) {
      let schema = template.template_schema || {};
      if (typeof schema === "string") {
        try {
          schema = schema.trim() === "" ? {} : JSON.parse(schema);
        } catch (e) {
          schema = {};
        }
      }
      setEditTemplate({ ...template, template_schema: schema });
    }
  }, [template]);

  const handleChange = (e) => {
    setEditTemplate({ ...editTemplate, [e.target.name]: e.target.value });
  };

  const save = () => {
    let schemaToSave = editTemplate.template_schema;

    // If user typed in textarea → parse it
    if (typeof schemaToSave === "string") {
      if (schemaToSave.trim() === "" || schemaToSave.trim() === "{}") {
        schemaToSave = null;
      } else {
        try {
          schemaToSave = JSON.parse(schemaToSave);
        } catch (e) {
          Swal.fire("Invalid JSON", "Schema mein valid JSON daalo!", "error");
          return;
        }
      }
    }

    const finalTemplate = {
      ...editTemplate,
      template_schema: schemaToSave,
      template_description: editTemplate.template_description || null,
      template_preview: editTemplate.template_preview || null,
    };

    onSave(finalTemplate);
  };

  const cancelEdit = () => {
    if (isAdd) {
      onClose();
    } else {
      setEditTemplate(template);
      setMode("view");
    }
  };

  return (
    <div className="job-modal-overlay">
      <div className="job-modal">
        <div className="job-modal-header">
          <h2>
            {isAdd
              ? "Add New Template"
              : `Template: ${
                  editTemplate.template_name || editTemplate.template_id
                }`}
          </h2>
          <div className="job-modal-actions">
            {!isEdit && !isAdd && (
              <button onClick={() => setMode("edit")} title="Edit">
                <FaEdit />
              </button>
            )}
            {isEdit && (
              <>
                <button onClick={save} title="Save">
                  <FaSave />
                </button>
                <button onClick={cancelEdit} title="Cancel">
                  <FaTimes />
                </button>
              </>
            )}
            <button className="job-modal-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        <table className="job-details-table">
          <tbody>
            {!isAdd && (
              <tr>
                <td>Template ID</td>
                <td>{editTemplate.template_id}</td>
              </tr>
            )}

            <tr>
              <td>Template Name</td>
              <td>
                {isEdit ? (
                  <input
                    type="text"
                    name="template_name"
                    value={editTemplate.template_name || ""}
                    onChange={handleChange}
                    required
                  />
                ) : (
                  editTemplate.template_name
                )}
              </td>
            </tr>

            <tr>
              <td>Description</td>
              <td>
                {isEdit ? (
                  <textarea
                    name="template_description"
                    value={editTemplate.template_description || ""}
                    onChange={handleChange}
                    rows="4"
                  />
                ) : (
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {editTemplate.template_description || "-"}
                  </div>
                )}
              </td>
            </tr>

            <tr>
              <td>Preview Image URL</td>
              <td>
                {isEdit ? (
                  <input
                    type="url"
                    name="template_preview"
                    value={editTemplate.template_preview || ""}
                    onChange={handleChange}
                    placeholder="https://example.com/preview.jpg"
                  />
                ) : editTemplate.template_preview ? (
                  <img
                    src={editTemplate.template_preview}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      borderRadius: "8px",
                    }}
                  />
                ) : (
                  "No preview"
                )}
              </td>
            </tr>

            <tr>
              <td>Schema (JSON)</td>
              <td>
                {isEdit ? (
                  <textarea
                    name="template_schema"
                    value={
                      typeof editTemplate.template_schema === "object" &&
                      editTemplate.template_schema !== null
                        ? JSON.stringify(editTemplate.template_schema, null, 2)
                        : editTemplate.template_schema || ""
                    }
                    onChange={handleChange}
                    rows="12"
                    placeholder='{ "heading": "string", "pointsItems": [{ "title": "string", "text": "string" }] }'
                    style={{ fontFamily: "monospace", fontSize: "0.9em" }}
                  />
                ) : (
                  <pre className="temp-json">
                    {editTemplate.template_schema &&
                    Object.keys(editTemplate.template_schema).length > 0
                      ? JSON.stringify(editTemplate.template_schema, null, 2)
                      : "{}"}
                  </pre>
                )}
              </td>
            </tr>

            <tr>
              <td>Template Code</td>
              <td>
                {isEdit ? (
                  <textarea
                    name="template_code"
                    value={editTemplate.template_code || ""}
                    onChange={handleChange}
                    rows="20"
                    style={{ fontFamily: "monospace", width: "100%" }}
                    required
                  />
                ) : (
                  <pre className="temp-code">
                    <code>{editTemplate.template_code}</code>
                  </pre>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
